import React, { useCallback, useState } from 'react';

import { TeamUUID } from '../../../../../../teams/teamsTypes';
import {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsFilterFields,
  FetchMaterialsFilters
} from '../../../../../../materials/materialsTypes';
import { IsDisabled } from '../../../../../../../types';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import { MaterialsPermissions } from '../../../../../../materials/materialsConstants';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockMaterialsFiltersFavoritesFilterProps {
  teamUuid?: TeamUUID;
  materialsFilters: FetchMaterialsFilters;
  disabled?: IsDisabled;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
}

const favoriteFieldName = FetchMaterialsFilterFields.FAVORITE;
const teamIdFavoriteFieldName = FetchMaterialsFilterFields.TEAM_ID_FAVORITE;
const currentTeamFavoriteFieldName =
  FetchMaterialsFilterFields.CURRENT_TEAM_FAVORITE;

function ThreeDStockMaterialsFiltersFavoritesFilter({
  teamUuid,
  materialsFilters,
  disabled,
  changeMaterialsFilters
}: ThreeDStockMaterialsFiltersFavoritesFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const isFavorites = materialsFilters?.[favoriteFieldName];
  const handleFavoriteClick = useCallback(() => {
    if (isFavorites) {
      changeMaterialsFilters({}, [favoriteFieldName]);
    } else {
      changeMaterialsFilters({ [favoriteFieldName]: true }, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }
  }, [isFavorites, changeMaterialsFilters]);

  const isTeamFavorites = materialsFilters?.[teamIdFavoriteFieldName];
  const isCurrentTeamFavorites =
    materialsFilters?.[currentTeamFavoriteFieldName];

  const handleTeamFavoriteClick = useCallback(() => {
    if (isTeamFavorites || isCurrentTeamFavorites) {
      return changeMaterialsFilters({}, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }

    const filterName = teamUuid
      ? teamIdFavoriteFieldName
      : currentTeamFavoriteFieldName;

    const filterValue = teamUuid ? teamUuid : true;

    changeMaterialsFilters({ [filterName]: filterValue }, [favoriteFieldName]);
  }, [
    isTeamFavorites,
    isCurrentTeamFavorites,
    changeMaterialsFilters,
    teamUuid
  ]);

  return (
    <CheckPermissions
      actions={[
        MaterialsPermissions.READ_MATERIALS_FAVORITE_CHECKBOX_FILTER,
        MaterialsPermissions.READ_MATERIALS_MY_COMPANY_FAVORITE_CHECKBOX_FILTER
      ]}
    >
      <div className="p-4">
        <div className="flex flex-col gap-3">
          <ButtonHelper
            className="flex items-center gap-2 rounded-sm"
            dataGa="favorites"
            onClick={toggleIsOpen}
          >
            <span className="text-sm font-semibold text-left flex-1">
              <Translate id={words.favorites} />
            </span>

            <Icon
              icon={
                isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP
              }
              className="h-5 w-5 text-gray-500"
            />
          </ButtonHelper>

          {isOpen && (
            <div className="flex flex-wrap gap-2">
              <CheckPermissions
                action={
                  MaterialsPermissions.READ_MATERIALS_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureIconButtonHelper
                  className={
                    isFavorites
                      ? 'border rounded text-sm flex items-center h-8 box-content border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'border rounded text-sm flex items-center h-8 box-content border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  }
                  icon={IconsEnum.HEART_SOLID}
                  iconClassName="-mr-2 ml-2 h-4 w-4 text-pink-500"
                  i18nText={words.favorites}
                  i18nTextClassName="px-3 whitespace-nowrap"
                  onClick={handleFavoriteClick}
                  dataGa="favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  MaterialsPermissions.READ_MATERIALS_MY_COMPANY_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureButtonHelper
                  className={
                    isTeamFavorites || isCurrentTeamFavorites
                      ? 'px-3 border rounded text-sm flex items-center h-8 box-content border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'px-3 border rounded text-sm flex items-center h-8 box-content border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  }
                  i18nText={words.companyFavorites}
                  i18nTextClassName="inline"
                  onClick={handleTeamFavoriteClick}
                  dataGa="team-favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>
            </div>
          )}
        </div>
      </div>
    </CheckPermissions>
  );
}

export default ThreeDStockMaterialsFiltersFavoritesFilter;
